import $ from 'jquery';

const CONFIG = {
    PASSWORD_FIELD: '.js-passwordToggle-pw',
    TOGGLE_BUTTON: '.js-passwordToggle-btn'
};

/**
 * @fileOverview View handles updating the visual elements on the page
 */
export default class PasswordToggleView {
    /**
     * Construct the view and run initialization
     *
     * @param element
     */
    constructor(element) {
        /**
         * Check to see if elements are passed in as jQuery objects or strings
         */
        this.$element = (element instanceof $) ? element : $(element);

        /**
         * If element doesn't exist, exit early
         */
        if (!this.$element.length) {
            return;
        }

        this.init();
    }

    /**
     * Initialize the view
     *
     * @method init
     */
    init() {
        this.isEnabled = false;

        return this.setupHandlers()
            .createChildren()
            .layout()
            .enable();
    }

    /**
     * Binds the scope of handler functions
     * Should be run once on view initialization
     *
     * @method setupHandlers
     * @returns {PasswordToggleView}
     */
    setupHandlers() {
        this.handleClick = this.onClick.bind(this);

        return this;
    }

    /**
     * Create any child objects and references to DOM elements
     * Should only be run once on init of the view
     *
     * @method createChildren
     * @returns {PasswordToggleView}
     */
    createChildren() {
        this.$toggleBtn = $(CONFIG.TOGGLE_BUTTON);
        return this;
    }

    /**
     * Update the DOM to reflect the current state of the model
     *
     * @method layout
     * @returns {PasswordToggleView}
     */
    layout() {
        return this;
    }

    /**
     * Disable, update the DOM, and then re-enable to create handlers for new DOM
     *
     * @method render
     */
    render() {
        this.disable();
        this.layout();
        this.enable();
    }

    /**
     * Enables the view
     * Performs binding to handlers
     * Exits early if already enabled
     *
     * @method enable
     * @returns {PasswordToggleView}
     */
    enable() {
        if (this.isEnabled) {
            return this;
        }

        this.isEnabled = true;

        for (let i = 0; i < this.$toggleBtn.length; i++) {
            this.$toggleBtn[i].addEventListener('click', this.handleClick);
        }

        return this;
    }

    /**
     * Disables the view
     * Tears down any event binding to handlers
     * Exits early if it is already disabled
     *
     * @method disable
     * @returns {PasswordToggleView}
     */
    disable() {
        if (!this.isEnabled) {
            return this;
        }

        for (let i = 0; i < this.$toggleBtn.length; i++) {
            this.$toggleBtn[i].removeEventListener('click', this.handleClick);
        }

        this.isEnabled = false;

        return this;
    }

    /**
     * Destroys the view
     * Tears down any events, handlers, elements
     * Should be called when the object should be left unused
     *
     * @method destroy
     * @returns {PasswordToggleView}
     */
    destroy() {
        this.disable();

        return this;
    }

    /**
     * Handles click event
     *
     * @param event
     * @returns {PasswordToggleView}
     */
    onClick(event) {
        const $target = $(event.currentTarget);
        const $container = $target.closest(this.$element);
        const $currentField = $container.find(CONFIG.PASSWORD_FIELD);
        const $currentBtn = $container.find(CONFIG.TOGGLE_BUTTON);

        if ($currentField.length && $currentBtn.length) {
            this.togglePassword($currentField, $currentBtn);
        }

        return this;
    }

    /**
     * Toggles the password to be visible or not, and changes button text
     *
     * @param {jQuery|HTMLElement} $pwField
     * @param {jQuery|HTMLElement} $toggleBtn
     * @returns {PasswordToggleView}
     */
    togglePassword($pwField, $toggleBtn) {
        const newType = ($pwField.prop('type') === 'password') ? 'text' : 'password';
        $pwField.prop('type', newType);

        const btnText = ($toggleBtn.html().toLowerCase() === 'show') ? 'hide' : 'show';
        $toggleBtn.html(btnText);

        $pwField.focus();

        return this;
    }
}
